import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, XCircle, Mail, Building2, MapPin } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";

const AREAS = [
  "Request Demo",
  "Partnership",
  "Investor Inquiry",
  "Media",
  "Security",
  "Compliance",
  "Documentation Request",
  "Audit & Due Diligence",
  "Other",
];

const OFFICES = [
  {
    key: "hq",
    badge: "Headquarters",
    city: "New York",
    lines: ["Vaultavo HQ", "33 West End Ave #20F", "New York, NY 10023"],
  },
  {
    key: "uk",
    badge: "UK Office",
    city: "London",
    lines: ["184 Shepherds Bush Rd", "London W6 7PF", "United Kingdom"],
  },
  {
    key: "hk",
    badge: "Hong Kong Office",
    city: "Hong Kong",
    lines: [
      "Unit 9, 12/F, Genesis",
      "33-35 Wong Chuk Hang Road",
      "Wong Chuk Hang, Hong Kong",
    ],
  },
];

const initial = {
  name: "",
  company: "",
  role: "",
  email: "",
  area_of_interest: "Request Demo",
  message: "",
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const requested = searchParams.get("area");
    if (requested && AREAS.includes(requested)) {
      setForm((f) => ({ ...f, area_of_interest: requested }));
    }
  }, [searchParams]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    setSuccess(false);
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      toast.success("Thank you!", {
        icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
        description: "The Vaultavo team will be in touch shortly.",
      });
      setForm(initial);
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
      toast.error("Something went wrong!", {
        icon: <XCircle className="h-5 w-5 text-red-500" />,
        description: typeof detail === "string" ? detail : "Submission failed.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-white" data-testid="contact-page">
      <Toaster position="top-center" richColors />

      <section className="pt-32 md:pt-40 pb-16 bg-radial-soft relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Contact
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A] max-w-4xl">
              Speak to Vaultavo.
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-7 max-w-2xl text-lg text-[#06102A]/70">
              For institutional partnerships, pilots, investment discussions, or technology
              demonstrations.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 space-y-5">
            <Reveal>
              <div className="rounded-2xl border border-[#06102A]/10 bg-white p-7">
                <Building2 className="h-5 w-5 text-[#06102A]/60" />
                <p className="mt-5 text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                  Institutional inquiries
                </p>
                <p className="mt-3 text-sm text-[#06102A]/85 leading-relaxed">
                  We work with banks, asset managers, exchanges, tokenization platforms, stablecoin
                  issuers, custodians, and governments.
                </p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="rounded-2xl border border-[#06102A]/10 bg-white p-7">
                <Mail className="h-5 w-5 text-[#06102A]/60" />
                <p className="mt-5 text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                  Direct email
                </p>
                <a
                  href="mailto:hello@vaultavo.com"
                  data-testid="contact-email-link"
                  className="mt-3 inline-block text-sm text-[#06102A] hover:text-[#3B82F6] transition-colors vt-link"
                >
                  hello@vaultavo.com
                </a>
              </div>
            </Reveal>
            <Reveal delay={160}>
              <div className="rounded-2xl dark-panel p-7 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
                <p className="text-xs uppercase tracking-[0.22em] font-semibold text-white/65 relative">
                  Why Vaultavo
                </p>
                <p className="mt-4 text-sm leading-relaxed text-white/80 relative">
                  Identity replaces secrets. Hardware enforces control. Policy governs behaviour —
                  delivered as one unified system.
                </p>
              </div>
            </Reveal>
          </aside>

          <div className="lg:col-span-8">
            <Reveal>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="rounded-3xl border border-[#06102A]/10 bg-white p-8 md:p-10 shadow-[0_30px_60px_-40px_rgba(6,16,42,0.25)]"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    label="Name"
                    testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                    placeholder="Full name"
                  />
                  <Field
                    label="Company"
                    testid="contact-input-company"
                    value={form.company}
                    onChange={update("company")}
                    required
                    placeholder="Institution"
                  />
                  <Field
                    label="Role"
                    testid="contact-input-role"
                    value={form.role}
                    onChange={update("role")}
                    required
                    placeholder="Title"
                  />
                  <Field
                    label="Email"
                    testid="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    type="email"
                    placeholder="name@institution.com"
                  />
                </div>

                <div className="mt-5">
                  <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                    Area of interest
                  </p>

                  {/* Mobile: styled select */}
                  <div className="mt-3 sm:hidden relative">
                    <select
                      data-testid="contact-area-select"
                      value={form.area_of_interest}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, area_of_interest: e.target.value }))
                      }
                      className="w-full appearance-none rounded-xl border border-[#06102A]/15 bg-white px-4 py-3 pr-10 text-[15px] text-[#06102A] focus:outline-none focus:border-[#06102A] focus:ring-2 focus:ring-[#06102A]/10 transition-all"
                    >
                      {AREAS.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                    <svg
                      aria-hidden="true"
                      className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#06102A]/55"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>

                  {/* Tablet+ : pill row */}
                  <div className="mt-3 hidden sm:flex flex-wrap gap-2">
                    {AREAS.map((a) => {
                      const active = form.area_of_interest === a;
                      return (
                        <button
                          type="button"
                          key={a}
                          data-testid={`contact-area-${a.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => setForm((f) => ({ ...f, area_of_interest: a }))}
                          className={`rounded-full px-4 py-2 text-xs font-medium border transition-all ${
                            active
                              ? "bg-[#06102A] text-white border-[#06102A]"
                              : "bg-white text-[#06102A]/75 border-[#06102A]/15 hover:border-[#06102A]"
                          }`}
                        >
                          {a}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block">
                    <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                      Message
                    </span>
                    <textarea
                      data-testid="contact-input-message"
                      required
                      value={form.message}
                      onChange={update("message")}
                      rows={5}
                      placeholder="Briefly describe your use case, pilot scope, or area of inquiry."
                      className="mt-3 w-full rounded-xl border border-[#06102A]/15 bg-white px-4 py-3 text-[15px] text-[#06102A] placeholder:text-[#06102A]/35 focus:outline-none focus:border-[#06102A] focus:ring-2 focus:ring-[#06102A]/10 transition-all resize-none"
                    />
                  </label>
                </div>

                <div className="mt-8 flex items-center justify-between gap-4">
                  <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="group inline-flex items-center gap-2 rounded-full bg-[#06102A] text-white text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-all disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Send message"}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>

                {success && (
                  <div
                    data-testid="contact-success"
                    className="mt-6 flex items-start gap-3 rounded-xl border border-[#3B82F6]/30 bg-[#3B82F6]/[0.06] p-4"
                  >
                    <CheckCircle2 className="h-5 w-5 text-[#3B82F6] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#06102A]">Submission received.</p>
                      <p className="text-xs text-[#06102A]/70 mt-1">
                        A member of the Vaultavo team will follow up shortly.
                      </p>
                    </div>
                  </div>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-28 md:pb-36" data-testid="offices-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Global Offices
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A] max-w-3xl">
              Three regions. One trust fabric.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {OFFICES.map((o, i) => (
              <Reveal key={o.key} delay={i * 100}>
                <article
                  data-testid={`office-card-${o.key}`}
                  className="group relative h-full rounded-3xl border border-[#06102A]/10 bg-white p-8 md:p-9 hover:border-[#06102A] hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(6,16,42,0.25)] transition-all duration-500"
                >
                  <div
                    className="absolute -top-px left-8 right-8 h-px"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(59,130,246,0.5), transparent)",
                    }}
                  />
                  <div className="flex items-center justify-between">
                    <div className="h-10 w-10 rounded-xl bg-[#06102A]/[0.04] grid place-items-center">
                      <MapPin className="h-4 w-4 text-[#06102A]" strokeWidth={1.7} />
                    </div>
                    <span className="font-mono-vt text-xs text-[#06102A]/45">
                      {String(i + 1).padStart(2, "0")} / 03
                    </span>
                  </div>
                  <p className="mt-7 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#F97316]">
                    {o.badge}
                  </p>
                  <h3 className="mt-2 font-display text-2xl md:text-[28px] font-medium text-[#06102A]">
                    {o.city}
                  </h3>
                  <address className="not-italic mt-5 space-y-1 text-sm text-[#06102A]/75 leading-relaxed">
                    {o.lines.map((l) => (
                      <p key={l}>{l}</p>
                    ))}
                  </address>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, testid, value, onChange, required, type = "text", placeholder }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
      {label}
    </span>
    <input
      data-testid={testid}
      value={value}
      onChange={onChange}
      required={required}
      type={type}
      placeholder={placeholder}
      className="mt-3 w-full rounded-xl border border-[#06102A]/15 bg-white px-4 py-3 text-[15px] text-[#06102A] placeholder:text-[#06102A]/35 focus:outline-none focus:border-[#06102A] focus:ring-2 focus:ring-[#06102A]/10 transition-all"
    />
  </label>
);
